import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, type MeResponse } from '@citibox-panel/api-client';
import { hasAnyKnownPermission } from '@/lib/access';
import { setRecheckStatus, useAccessRecheck } from '@/lib/accessRecheck';
import { retryUnlessRefused } from '@/lib/queryClient';
import { SessionProvider, toSession } from '@/lib/session';
import { signOutOfPanel } from '@/lib/signOut';
import { AccessStateScreen } from './AccessStateScreen';

export interface SessionGateProps {
  uid: string;
  email: string | null;
  fetchMe: () => Promise<MeResponse>;
  children: ReactNode;
}

const isUnauthorized = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 401;

interface FailureScreenProps {
  failure: unknown;
  onRetry: () => void;
  retrying: boolean;
  failures: number;
}

// A failed /me: an expired session can only sign out; any other failure can also be retried.
function FailureScreen({ failure, onRetry, retrying, failures }: FailureScreenProps): ReactElement {
  if (isUnauthorized(failure)) {
    return <AccessStateScreen variant="session-expired" onSignOut={signOutOfPanel} />;
  }
  return (
    <AccessStateScreen
      variant="error"
      onSignOut={signOutOfPanel}
      onRetry={onRetry}
      retrying={retrying}
      failures={failures}
    />
  );
}

// Reads /me for the signed-in person and keeps its last failure on screen while a retry runs.
function useMe(uid: string, fetchMe: () => Promise<MeResponse>) {
  const query = useQuery({
    queryKey: ['me', uid],
    queryFn: fetchMe,
    // Once per session: loaded data never goes stale, so neither a reconnect nor a remount reads it
    // again, and a revoked permission takes effect on reload or a new sign-in. A /me that failed
    // has no data and still counts as stale, so it can recover.
    staleTime: Infinity,
    // fetchMe already retried a 401 with a fresh token; another round only delays the way out. The
    // rest follows the shared rule: one retry for a transient failure, none for a permission refusal.
    retry: (failureCount, error) => !isUnauthorized(error) && retryUnlessRefused(failureCount, error),
    // Offline, the default mode pauses the read and leaves «Cargando…» with no way out. A check
    // that cannot reach the server is a failure to check: it runs, fails, and offers «Reintentar».
    networkMode: 'always',
  });
  // TanStack Query clears `error` and puts a query without data back to `pending` while it
  // refetches, so the last failure is kept here: a retry in flight stays on the failure's own screen,
  // busy, instead of falling back to «Cargando…».
  const [lastError, setLastError] = useState<unknown>(null);
  if (query.error !== null && query.error !== lastError) setLastError(query.error);
  const failure = query.error ?? lastError;
  return { query, failure };
}

// Loads /me once per session and decides what the authenticated person sees. A failure never grants.
export function SessionGate({ uid, email, fetchMe, children }: SessionGateProps): ReactElement {
  const { query, failure } = useMe(uid, fetchMe);
  const session = useMemo(() => (query.data === undefined ? null : toSession(query.data)), [query.data]);
  const { recheck } = useAccessRecheck();
  const failureScreen = (onRetry: () => void): ReactElement => (
    <FailureScreen
      failure={failure}
      onRetry={onRetry}
      retrying={query.isFetching}
      failures={query.errorUpdateCount}
    />
  );

  // After the server refused a view, /me is re-read; if that re-read fails, the grants on screen are
  // exactly what the server just contradicted, so the failure is shown instead of them, until a
  // retry reads /me again.
  if (recheck === 'failed') {
    return failureScreen(() => {
      void query.refetch().then((result) => {
        if (result.status === 'success') setRecheckStatus('idle');
      });
    });
  }
  // Otherwise data wins over a later background error, so a transient refetch failure never unmounts
  // the app; and «no permissions» is data, so a refetch that lands there replaces the old grants.
  if (session !== null) {
    if (!hasAnyKnownPermission(session.permissions)) {
      return <AccessStateScreen variant="no-access" email={email} onSignOut={signOutOfPanel} />;
    }
    return <SessionProvider value={session}>{children}</SessionProvider>;
  }
  if (failure !== null) {
    return failureScreen(() => {
      void query.refetch();
    });
  }
  return <AccessStateScreen variant="loading" />;
}
